<script setup lang="ts">
import FloatLabel from 'primevue/floatlabel'
import InputMask from 'primevue/inputmask'
import InputText from 'primevue/inputtext'
import { computed, ref, useId } from 'vue'

/**
 * Поле ввода из макета (компонент «Поле для ввода»): 50px, r20, белое
 * с бордером #EFEFEF, значение 16 Medium на отступе 13px.
 *
 * Лейбл — PrimeVue FloatLabel в варианте `on`: пусто — 16px по центру поля,
 * фокус или заполнено — 12px в белой плашке на верхней границе.
 *
 * С `mask` вместо InputText рисуется InputMask: он сам держит каретку при
 * правке внутри значения и при вставке. `prefix` — несъедаемая часть слева
 * (код страны), в модель она не попадает.
 */
interface Props {
  label: string
  type?: string
  mask?: string
  prefix?: string
  /** Поле только показывает значение: менять его здесь нельзя */
  disabled?: boolean
}

const props = defineProps<Props>()

const value = defineModel<string>({ default: '' })

const id = useId()
const focused = ref(false)

const isFilled = computed(() => value.value.length > 0)

/** Префикс появляется вместе с поднятым лейблом, иначе он висит в пустом поле. */
const showPrefix = computed(() => Boolean(props.prefix) && (isFilled.value || focused.value))
</script>

<template>
  <FloatLabel class="float-field" variant="on">
    <span class="float-field__control" :class="{ 'float-field__control--locked': props.disabled }">
      <span v-if="showPrefix" class="float-field__prefix">{{ props.prefix }}</span>

      <InputMask
        v-if="props.mask"
        :id="id"
        v-model="value"
        :class="{ 'p-filled': isFilled }"
        :mask="props.mask"
        :disabled="props.disabled"
        unmask
        :auto-clear="false"
        variant="outlined"
        type="tel"
        inputmode="numeric"
        @focus="focused = true"
        @blur="focused = false"
      />
      <InputText
        v-else
        :id="id"
        v-model="value"
        :class="{ 'p-filled': isFilled }"
        :type="props.type ?? 'text'"
        :disabled="props.disabled"
        variant="outlined"
        @focus="focused = true"
        @blur="focused = false"
      />
    </span>

    <label :for="id">{{ props.label }}</label>
  </FloatLabel>
</template>

<style scoped lang="scss">
.float-field {
  width: 100%;

  /* Покой: лейбл вместо placeholder — 16px, отступ такой же, как у значения */
  :deep(label) {
    padding: 0 var(--space-3);
    font-size: 16px;
  }

  /* Рамку и фон рисует обёртка: внутри может стоять ещё и префикс */
  &__control {
    display: flex;
    align-items: center;
    height: 50px;
    /* минус бордер: в макете значение стоит на 13px от внешнего края */
    padding: 0 calc(var(--field-padding-x) - 1px);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-xl);
    background: var(--surface-card);
    cursor: text;
  }

  /* Поле только для чтения: приглушаем, но оставляем читаемым */
  &__control--locked {
    background: var(--surface-muted);
    cursor: default;
  }

  &__prefix {
    flex: none;
    color: var(--text-color);
    font-size: 16px;
    font-weight: var(--fw-medium);
    line-height: 24px;
  }

  /*
   * Заблокированный инпут: и iOS, и PrimeVue гасят такие до серости, да ещё
   * своим `-webkit-text-fill-color`. Значение возвращаем в обычный чёрный —
   * оно рядом с таким же чёрным префиксом «+998», и разнобой выглядел бы
   * ошибкой. О том, что поле не правят, говорит фон.
   */
  :deep(input:disabled) {
    opacity: 1;
    color: var(--text-color);
    -webkit-text-fill-color: var(--text-color);
  }

  /* Инпут внутри обёртки без собственной геометрии и состояний PrimeVue */
  :deep(input) {
    width: 100%;
    min-width: 0;
    height: auto;
    min-height: 0;
    padding: 0;
    border: 0;
    border-radius: 0;
    background: none;
    box-shadow: none;
    color: var(--text-color);
    font-family: inherit;
    font-size: 16px;
    font-weight: var(--fw-medium);
    line-height: 24px;
    outline: none;

    &:enabled:hover,
    &:enabled:focus {
      border: 0;
      background: none;
      box-shadow: none;
    }
  }
}
</style>
